import React, { useEffect, useMemo, useState } from "react";
import { api, formatINR } from "@/lib/api";
import StatCard from "@/components/StatCard";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Label } from "@/components/ui/label";

const MONTH_NAMES = [
  "January", "February", "March", "April", "May", "June",
  "July", "August", "September", "October", "November", "December",
];
const pad = (n) => String(n).padStart(2, "0");

export default function Savings() {
  const [vehicles, setVehicles] = useState([]);
  const [vehicleId, setVehicleId] = useState("__all");
  const [data, setData] = useState(null);

  const now = new Date();
  const [year, setYear] = useState(String(now.getFullYear()));
  const [month, setMonth] = useState("__all");

  const yearOptions = useMemo(() => {
    const years = [];
    for (let y = now.getFullYear(); y >= now.getFullYear() - 5; y--) {
      years.push(String(y));
    }
    return years;
  }, [now]);

  const range = useMemo(() => {
    const y = Number(year);
    if (month === "__all") {
      return { start: `${y}-01-01`, end: `${y}-12-31` };
    }
    const m = Number(month);
    const lastDay = new Date(y, m, 0).getDate();
    return { start: `${y}-${pad(m)}-01`, end: `${y}-${pad(m)}-${pad(lastDay)}` };
  }, [year, month]);

  useEffect(() => {
    api.get("/vehicles").then((r) => setVehicles(r.data));
  }, []);

  useEffect(() => {
    const params = { start: range.start, end: range.end };
    if (vehicleId === "__all") {
      api.get("/savings", { params }).then((r) => setData(r.data));
    } else {
      api.get(`/savings/${vehicleId}`, { params }).then((r) => setData(r.data));
    }
  }, [vehicleId, range.start, range.end]);

  const isAll = vehicleId === "__all";

  return (
    <div className="space-y-6" data-testid="savings-page">
      <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4">
        <div>
          <div className="label-eyebrow">Reserve</div>
          <h1 className="font-display text-4xl md:text-5xl font-black tracking-tighter mt-1">
            Savings Fund
          </h1>
          <p className="text-sm text-gray-500 mt-1">
            Daily savings & debits per vehicle. Balances may go negative.
          </p>
        </div>
        <div className="grid grid-cols-3 sm:flex sm:flex-wrap gap-2 sm:items-end">
          <div>
            <Label className="label-eyebrow">Year</Label>
            <Select value={year} onValueChange={(v) => { setYear(v); setMonth("__all"); }}>
              <SelectTrigger className="mt-1 w-full sm:w-[110px]" data-testid="savings-year-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {yearOptions.map((y) => (
                  <SelectItem key={y} value={y}>{y}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="label-eyebrow">Month</Label>
            <Select value={month} onValueChange={setMonth}>
              <SelectTrigger className="mt-1 w-full sm:w-[150px]" data-testid="savings-month-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="__all">All months</SelectItem>
                {MONTH_NAMES.map((name, i) => (
                  <SelectItem key={name} value={String(i + 1)}>
                    {name.slice(0, 3)} {String(year).slice(-2)}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="col-span-3 sm:col-auto">
            <Label className="label-eyebrow">Vehicle</Label>
            <Select value={vehicleId} onValueChange={setVehicleId}>
              <SelectTrigger className="mt-1 w-full sm:w-[260px]" data-testid="savings-vehicle-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="__all">All vehicles</SelectItem>
                {vehicles.map((v) => (
                  <SelectItem key={v.id} value={v.id}>
                    {v.name} · {v.registration_number}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>
      </div>
      <p className="text-xs text-gray-500 font-mono" data-testid="savings-range-label">
        {range.start} → {range.end}
      </p>

      {data && (
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <StatCard
            label="Total Savings"
            value={formatINR(data.total_credit || 0)}
            accent="text-emerald-700"
            testid="savings-total-credit"
          />
          <StatCard
            label="Total Debits"
            value={formatINR(data.total_debit || 0)}
            accent="text-red-600"
            testid="savings-total-debit"
          />
          <StatCard
            label="Net Balance"
            value={formatINR(data.balance || 0)}
            accent={(data.balance || 0) >= 0 ? "text-emerald-700" : "text-red-600"}
            sub={(data.balance || 0) < 0 ? "Negative — top up suggested" : "Healthy"}
            testid="savings-balance"
          />
        </div>
      )}

      {isAll && data?.per_vehicle && (
        <div className="card-swiss p-5" data-testid="savings-by-vehicle">
          <div className="label-eyebrow mb-2">By Vehicle</div>
          <h3 className="font-display text-xl font-bold tracking-tight mb-4">
            Per-vehicle Savings
          </h3>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-gray-200 text-left text-xs uppercase tracking-wider text-gray-500">
                  <th className="py-2 pr-4 font-semibold">Vehicle</th>
                  <th className="py-2 px-2 text-right font-semibold">Credits</th>
                  <th className="py-2 px-2 text-right font-semibold">Debits</th>
                  <th className="py-2 px-2 text-right font-semibold">Balance</th>
                </tr>
              </thead>
              <tbody>
                {data.per_vehicle.map((row) => (
                  <tr key={row.vehicle.id} className="border-b border-gray-100 hover-row">
                    <td className="py-3 pr-4">
                      <div className="font-semibold">{row.vehicle.name}</div>
                      <div className="text-xs text-gray-500 font-mono">
                        {row.vehicle.registration_number}
                      </div>
                    </td>
                    <td className="py-3 px-2 text-right font-mono text-emerald-700">
                      {formatINR(row.credit)}
                    </td>
                    <td className="py-3 px-2 text-right font-mono text-red-600">
                      {formatINR(row.debit)}
                    </td>
                    <td
                      className={`py-3 px-2 text-right font-mono font-semibold ${
                        row.balance < 0 ? "text-red-600" : ""
                      }`}
                    >
                      {formatINR(row.balance)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {!isAll && data?.ledger && (
        <div className="card-swiss p-5" data-testid="savings-ledger">
          <div className="label-eyebrow mb-2">Ledger</div>
          <h3 className="font-display text-xl font-bold tracking-tight mb-4">
            {data.vehicle?.name} — Daily Movements
          </h3>
          {data.ledger.length === 0 ? (
            <div className="py-8 text-center text-sm text-gray-500">
              No savings activity yet for this vehicle.
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-gray-200 text-left text-xs uppercase tracking-wider text-gray-500">
                    <th className="py-2 pr-4 font-semibold">Date</th>
                    <th className="py-2 px-2 text-right font-semibold">Credit</th>
                    <th className="py-2 px-2 text-right font-semibold">Debit</th>
                    <th className="py-2 pl-2 text-left font-semibold">Remark</th>
                    <th className="py-2 px-2 text-right font-semibold">Balance</th>
                  </tr>
                </thead>
                <tbody>
                  {data.ledger.map((l, i) => (
                    <tr key={i} className="border-b border-gray-100 hover-row">
                      <td className="py-2 pr-4 font-mono">{l.date}</td>
                      <td className="py-2 px-2 text-right font-mono text-emerald-700">
                        {l.credit ? formatINR(l.credit) : "—"}
                      </td>
                      <td className="py-2 px-2 text-right font-mono text-red-600">
                        {l.debit ? formatINR(l.debit) : "—"}
                      </td>
                      <td className="py-2 pl-2 text-gray-600 truncate max-w-[260px]">
                        {l.remark || "—"}
                      </td>
                      <td
                        className={`py-2 px-2 text-right font-mono font-semibold ${
                          l.balance < 0 ? "text-red-600" : ""
                        }`}
                      >
                        {formatINR(l.balance)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
